import {
  createContext,
  useContext,
  useEffect,
  useState,
  type ReactNode,
} from "react";
import { Navigate } from "react-router-dom";
import { useTranslation } from "react-i18next";

import {
  probeAdminAccess,
  type AdminAccess,
} from "@/lib/admin-api";

/**
 * apps/web :: components/admin :: AdminGate
 *
 * Wraps any `/admin/*` route. On first mount it probes
 * `GET /api/admin/practice/questions?limit=0` via `probeAdminAccess()` and
 * classifies the result into four branches:
 *
 *   - 200  → `admin`        → render children, expose `useAdmin()` as true
 *   - 401  → `unauth`       → `<Navigate to="/login">` with returnTo
 *   - 403  → `not_admin`    → `<Navigate to="/dashboard">` + toast
 *   - 503  → `unavailable`  → render inline "temporarily unavailable" card
 *
 * The probe result is cached via a module-level `CACHED_ACCESS` so per-page
 * navigations inside `/admin/*` don't re-probe — per the Wave 6b spec,
 * "Cache the probe result for the lifetime of the session. Don't re-probe
 * per-page." `UserMenu` also reads this cache via `useAdmin()` to decide
 * whether to surface the "Admin" link on every authenticated surface
 * (list + editor + dashboard + settings). That cross-cutting read is why
 * the cache sits at module scope rather than hiding inside the component
 * tree — a provider-only hook would have `useAdmin()` throwing outside
 * the admin subtree. An in-flight probe also memoises, so `UserMenu` +
 * `<AdminGate>` mounting in the same render don't issue two fetches.
 *
 * Cache invalidation: the cache is intentionally not reset on route
 * transitions. It IS reset on sign-out (see `resetAdminCacheForTests`) —
 * the existing `onSignOut()` analytics helper is the anchor follow-up
 * ticket covers adding that call once WorkOS RBAC replaces the env
 * allowlist (see PR body, "follow-ups").
 */

interface AdminCtx {
  isAdmin: boolean;
  access: AdminAccess;
}

const AdminContext = createContext<AdminCtx | null>(null);

// Module-level cache shared across AdminGate mounts + UserMenu. Set once
// the first probe resolves; subsequent mounts skip the network hit.
let CACHED_ACCESS: AdminAccess | null = null;
let IN_FLIGHT: Promise<AdminAccess> | null = null;

/**
 * Test-only: reset the module-level cache. The production code path does
 * not export a cache-reset helper because the cache lifetime is the full
 * signed-in session by design.
 */
export function __resetAdminCacheForTests(): void {
  CACHED_ACCESS = null;
  IN_FLIGHT = null;
}

/**
 * Hook for surfaces (like UserMenu) that want to conditionally render
 * admin-specific UI without gating the whole subtree. Returns `false`
 * until the probe resolves. Outside an AdminGate subtree, falls back to
 * the module-level cache (which UserMenu reads on every authenticated
 * page — see the "Admin" link in `components/UserMenu.tsx`).
 */
export function useAdmin(): { isAdmin: boolean } {
  const ctx = useContext(AdminContext);
  if (ctx) return { isAdmin: ctx.isAdmin };
  return { isAdmin: CACHED_ACCESS === "admin" };
}

interface GateState {
  kind: "loading" | "done";
  access?: AdminAccess;
}

export default function AdminGate({ children }: { children: ReactNode }) {
  const { t } = useTranslation();
  const [state, setState] = useState<GateState>(() => {
    if (CACHED_ACCESS) return { kind: "done", access: CACHED_ACCESS };
    return { kind: "loading" };
  });

  useEffect(() => {
    // StrictMode runs every effect twice — if we used a `cancelled` flag
    // in the cleanup, the second render's cleanup would cancel the first
    // render's fetch and the state would never transition from loading.
    // Instead we dedupe via the module-level IN_FLIGHT promise, and drop
    // the setState when the cache already moved on.
    if (CACHED_ACCESS) {
      setState({ kind: "done", access: CACHED_ACCESS });
      return;
    }

    const inFlight = IN_FLIGHT ?? probeAdminAccess();
    IN_FLIGHT = inFlight;
    inFlight
      .then((access) => {
        CACHED_ACCESS = access;
        IN_FLIGHT = null;
        setState({ kind: "done", access });
      })
      .catch(() => {
        // Unexpected error (network drop, 500). Treat the session as not
        // admin so the UI degrades predictably — the dashboard toast tells
        // the user their admin check failed; they can retry by reloading.
        CACHED_ACCESS = "not_admin";
        IN_FLIGHT = null;
        setState({ kind: "done", access: "not_admin" });
      });
  }, []);

  if (state.kind === "loading") {
    return (
      <div
        role="status"
        aria-live="polite"
        aria-label={t("admin.gate.loading")}
        data-testid="admin-gate-loading"
        className="mx-auto mt-xl max-w-md p-md text-center text-neutral-700"
      >
        {t("admin.gate.loading")}
      </div>
    );
  }

  const access = state.access!;

  if (access === "unauth") {
    return <Navigate to="/login" replace state={{ returnTo: "/admin/questions" }} />;
  }

  if (access === "not_admin") {
    return (
      <Navigate
        to="/dashboard"
        replace
        state={{ toast: t("admin.gate.notAuthorizedToast") }}
      />
    );
  }

  if (access === "unavailable") {
    return (
      <section
        data-testid="admin-gate-unavailable"
        className="mx-auto mt-xl max-w-lg rounded-md border border-warning/40 bg-warning/5 p-md text-sm text-neutral-800"
        role="status"
      >
        {t("admin.gate.unavailable")}
      </section>
    );
  }

  return (
    <AdminContext.Provider value={{ isAdmin: true, access: "admin" }}>
      {children}
    </AdminContext.Provider>
  );
}
